import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import { createPortal } from 'react-dom';
import './AnimatedBorderButton.css';

interface AnimatedBorderButtonProps {
  onPressed: () => void;
  onStartPress?: () => void; // 버튼을 누르기 시작할 때 호출
  onIncomplete?: () => void;
  disabledCallback?: () => void;
  title: string;
  tooltipText?: string; // 말풍선 텍스트 추가
  duration?: number; // 초 단위 (기본값: 1초)
  width?: number;
  height?: number;
  borderRadius?: number;
  borderThickness?: number;
  initialBorderColor?: string;
  finalBorderColor?: string;
  backgroundColor?: string;
  textColor?: string;
  textPressedColor?: string;
  textCompletedColor?: string;
  disabled?: boolean;
  className?: string;
  hideTooltip?: boolean; // 툴팁을 숨길지 여부
}

export interface AnimatedBorderButtonRef {
  cancelProgress: () => void;
}

const AnimatedBorderButton = forwardRef<AnimatedBorderButtonRef, AnimatedBorderButtonProps>(({
  onPressed,
  onStartPress,
  onIncomplete,
  disabledCallback,
  title,
  tooltipText = '10초간 꾹 눌러서 입찰',
  duration = 10,
  width,
  height = 60,
  borderRadius = 8,
  borderThickness = 4,
  initialBorderColor = 'transparent',
  finalBorderColor = '#007bff',
  backgroundColor = '#f8f9fa',
  textColor = '#333',
  textPressedColor = '#007bff',
  textCompletedColor = '#28a745',
  disabled = false,
  className = '',
  hideTooltip = false
}, ref) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const startTimeRef = useRef<number>(0);
  const isAnimatingRef = useRef(false);

  const stopAnimation = useCallback(() => {
    isAnimatingRef.current = false;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsPressed(false);
    
    if (!isCompleted) {
      onIncomplete?.();
      // 애니메이션 리셋
      setTimeout(() => {
        setProgress(0);
      }, 200);
    }
  }, [isCompleted, onIncomplete]);

  // 외부에서 진행을 취소할 수 있는 함수를 노출
  useImperativeHandle(ref, () => ({
    cancelProgress: () => {
      devLog('🎯 [AnimatedBorderButton] cancelProgress 호출됨:', {
        isPressed,
        isCompleted,
        progress
      });
      
      isAnimatingRef.current = false;

      // 진행 중이든 아니든 무조건 모든 상태를 리셋
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // 모든 상태를 즉시 초기화
      setIsPressed(false);
      setIsCompleted(false);
      setProgress(0);
      
      devLog('🎯 [AnimatedBorderButton] cancelProgress 완료 - 모든 상태 리셋됨');
      
      // onIncomplete 콜백 호출 (취소되었음을 알림)
      if (isPressed && !isCompleted) {
        onIncomplete?.();
      }
    }
  }), [isPressed, isCompleted, progress, onIncomplete]);

  const startAnimation = () => {
    if (disabled) {
      disabledCallback?.();
      return;
    }

    // 이미 애니메이션이 진행 중이면 재진입 방지
    if (isAnimatingRef.current) {
      devWarn('🎯 [AnimatedBorderButton] startAnimation 재진입 방지 - 이미 진행 중');
      return;
    }

    isAnimatingRef.current = true;

    // 버튼을 누르기 시작할 때 콜백 호출
    onStartPress?.();

    setIsPressed(true);
    setIsCompleted(false);
    setProgress(0);
    startTimeRef.current = Date.now();

    // 60fps로 애니메이션 업데이트
    intervalRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const newProgress = Math.min(elapsed / duration, 1);
      setProgress(newProgress);

      if (newProgress >= 1) {
        completeAnimation();
      }
    }, 16); // ~60fps

    // 완료 시간에 맞춰 타이머 설정
    timeoutRef.current = setTimeout(() => {
      completeAnimation();
    }, duration * 1000);
  };

  const completeAnimation = () => {
    isAnimatingRef.current = false;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setProgress(1);
    setIsCompleted(true);
    setIsPressed(false);
    
    // 완료 콜백 호출
    setTimeout(() => {
      onPressed();
      // 애니메이션 초기화
      setTimeout(() => {
        setIsCompleted(false);
        setProgress(0);
      }, 500);
    }, 100);
  };

  // 컴포넌트가 마운트되고 활성화 상태일 때 말풍선 표시
  useEffect(() => {
    const currentTimeout = tooltipTimeoutRef.current;
    
    if (!disabled && !isPressed && !isCompleted && !hideTooltip) {
      setShowTooltip(true);
      // 위치 계산
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setTooltipPosition({
          top: rect.top - 50,
          left: rect.left + rect.width / 2
        });
      }
    } else {
      setShowTooltip(false);
      if (currentTimeout) {
        clearTimeout(currentTimeout);
        tooltipTimeoutRef.current = null;
      }
    }
    
    return () => {
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, [disabled, isPressed, isCompleted, hideTooltip]);

  // 말풍선 위치 업데이트 (스크롤이나 리사이즈 시)
  useEffect(() => {
    if (!showTooltip || !buttonRef.current) return;
    
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setTooltipPosition({
          top: rect.top - 50,
          left: rect.left + rect.width / 2
        });
      }
    };

    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showTooltip]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    startAnimation();
  };

  const handleMouseUp = () => {
    if (isPressed && !isCompleted) {
      stopAnimation();
    }
  };

  const handleMouseLeave = () => {
    if (isPressed && !isCompleted) {
      stopAnimation();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    startAnimation();
  };

  const handleTouchEnd = () => {
    if (isPressed && !isCompleted) {
      stopAnimation();
    }
  };

  useEffect(() => {
    return () => {
      isAnimatingRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const getCurrentTextColor = () => {
    if (disabled) return '#949494'; // AppColors.onBtnE_Disabled
    if (isCompleted) return textCompletedColor;
    if (isPressed) return textPressedColor;
    return textColor;
  };

  const getCurrentBackgroundColor = () => {
    if (disabled) return '#CCCCCC'; // AppColors.btnE_Disabled
    if (isPressed) return '#262626'; // AppColors.btnE_Hover
    return backgroundColor;
  };

  return (
    <div
      ref={buttonRef}
      className={`animated-border-button ${disabled ? 'disabled' : ''} ${className}`}
      style={{
        width: width ? `${width}px` : '100%',
        height: `${height}px`,
        borderRadius: `${borderRadius}px`,
        backgroundColor: getCurrentBackgroundColor(),
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        border: width ? 'none' : `${borderThickness}px solid ${finalBorderColor}`,
        borderColor: width ? 'transparent' : (progress > 0 ? finalBorderColor : initialBorderColor),
        transition: width ? 'none' : 'border-color 0.1s ease'
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onDragStart={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* 배경 */}
      <div
        className="button-background"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: `${borderRadius}px`,
          backgroundColor: getCurrentBackgroundColor(),
          zIndex: 1
        }}
      />
      
      {/* 애니메이션 보더 - width prop이 있을 때만 SVG 사용 */}
      {width && (
        <svg
          className="border-animation"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 2,
            pointerEvents: 'none'
          }}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <defs>
            <mask id="borderMask">
              <rect
                x="0"
                y="0"
                width={width}
                height={height}
                rx={borderRadius}
                ry={borderRadius}
                fill="white"
              />
              <rect
                x={borderThickness}
                y={borderThickness}
                width={width - borderThickness * 2}
                height={height - borderThickness * 2}
                rx={Math.max(0, borderRadius - borderThickness)}
                ry={Math.max(0, borderRadius - borderThickness)}
                fill="black"
              />
            </mask>
          </defs>
          
          {/* 초기 보더 */}
          <rect
            x="0"
            y="0"
            width={width}
            height={height}
            rx={borderRadius}
            ry={borderRadius}
            fill={initialBorderColor}
            mask="url(#borderMask)"
          />
          
          {/* 애니메이션 보더 */}
          <rect
            x="0"
            y="0"
            width={width}
            height={height}
            rx={borderRadius}
            ry={borderRadius}
            fill="none"
            stroke={finalBorderColor}
            strokeWidth={borderThickness}
            strokeDasharray={`${2 * (width + height - 4 * borderRadius + Math.PI * borderRadius)}`}
            strokeDashoffset={`${2 * (width + height - 4 * borderRadius + Math.PI * borderRadius) * (1 - progress)}`}
            style={{
              transition: 'none',
              transformOrigin: 'center',
              strokeLinecap: 'round'
            }}
          />
        </svg>
      )}
      
      {/* 텍스트 */}
      <span
        style={{
          position: 'relative',
          zIndex: 3,
          color: getCurrentTextColor(),
          fontWeight: isPressed || isCompleted ? 'bold' : 'normal',
          transition: 'color 0.2s ease, font-weight 0.2s ease',
          fontSize: '16px'
        }}
      >
        {title}
      </span>
      
      {/* Portal을 사용한 말풍선 툴팁 */}
      {showTooltip && createPortal(
        <div
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateX(-50%)',
            backgroundColor: '#48707b',
            color: '#ffffff',
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            opacity: 1,
            pointerEvents: 'none'
          }}
        >
          {tooltipText}
          {/* 말풍선 꼬리 */}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid #48707b'
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
});

AnimatedBorderButton.displayName = 'AnimatedBorderButton';

export default AnimatedBorderButton;
